
import { Vue, Component, Prop } from 'vue-property-decorator';

import ModalContent from '@/components/modal/ModalContentNew.vue';
import ModalFullScreen from '@/components/modal/ModalFullScreenNew.vue';
import FormDatepicker from '@/components/form/FormDatepickerNew.vue';
import INSERT_PROJECT_ACTIVITY_TIME from '@/apollo/projectActivities/InsertProjectActivityTime.gql';
import GET_PROJECT_ACTIVITY_TIMES from '@/apollo/projectActivities/GetProjectActivityTimes.gql';
import GET_PROJECT_ACTIVITY_REQUEST_MORE_HOURS from '@/apollo/projectActivities/GetRequstedHours.gql';
import UPDATE_PROJECT_ACTIVITY_TIME from '@/apollo/projectActivities/UpdateProjectActivityTime.gql';
import UPDATE_PROJECT_ACTIVITY_REQUEST_MORE_HOURS from '@/apollo/projectActivities/ProjectActivityRequestMoreHours.gql';
import DELETE_PROJECT_ACTIVITY_TIME from '@/apollo/projectActivities/DeleteProjectActivityTime.gql';
import GET_PROJECT_ACTIVITY_TIMES_BY_CONTRACTOR from '@/apollo/projectActivities/GetProjectActivityTimesByContractor.gql';
import moment from 'moment';
import GET_CONTRACTOR_ACTIVITIES from '@/apollo/projectActivities/GetContractorActivities.gql';
import GET_CONTRACTOR_PROJECTS from '@/apollo/projects/GetContractorProjects.gql';
import DELETE_PROJECT_ACTIVITY_REQUEST_MORE_HOURS from '@/apollo/projectActivities/DeleteProjectActivityRequestedHours.gql';
import DropdownFilter from '@/components/filters/DropdownFilterNew.vue';
import DropdownOption from '@/entities/DropdownOption';
import PROJECT_ACTIVITY_TIME_AGGREGATE from '@/apollo/projectActivities/ProjectActivityTimeAggregate.gql';
import PROJECT_ACTIVITY_REQUEST_MORE_HOURS_AGGREGATE from '@/apollo/projectActivities/ProjectActivityRequestMoreHoursAggregate.gql';
import {
	Project_Activity,
	Project,
	Project_Activity_Time,
	Project_Activity_Request_More_Hours,
	User,
} from '@/generated/graphql';
import convertSecondsToReadable from '@/utils/convertSecondsToReadable';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';
import TimeTrackingDelete from '@/components/icons/TimeTrackingDelete.vue';
import TimeTrackingEdit from '@/components/icons/TimeTrackingEdit.vue';
import NewTimePicker from '@/components/projects/timeTracker/NewTimePickerNew.vue';
import INSERT_PROJECT_ACTIVITY_MORE_HOURS_REQUEST from '@/apollo/projectActivities/InsertProjectActivityRequestMoreHours.gql';
import { NOTIFICATION } from '@/entities/Events';
import Message from '@/data/notificationsMessages';

@Component({
	components: {
		ModalContent,
		ModalFullScreen,
		LoadingOverlay,
		TimeTrackingDelete,
		TimeTrackingEdit,
		DropdownFilter,
		FormDatepicker,
		NewTimePicker,
	},
	apollo: {
		trackedTimes: {
			query: GET_PROJECT_ACTIVITY_TIMES,
			variables() {
				return {
					activityId: this.activity?.id,
				};
			},
			skip() {
				return !this.activity;
			},
			update: (data) => data.project_activity_time,
		},
		timeAggregate: {
			query: PROJECT_ACTIVITY_TIME_AGGREGATE,
			variables() {
				return {
					activityId: this.activity?.id,
				};
			},
			skip() {
				return !this.activity;
			},
			update: (data) =>
				data.project_activity_time_aggregate.aggregate.sum.time,
		},
		requestedHours: {
			query: GET_PROJECT_ACTIVITY_REQUEST_MORE_HOURS,
			variables() {
				return {
					activityId: this.activity?.id,
				};
			},
			skip() {
				return !this.activity;
			},
			update: (data) => data.project_activity_request_more_hours,
		},
		hoursAggregate: {
			query: PROJECT_ACTIVITY_REQUEST_MORE_HOURS_AGGREGATE,
			variables() {
				return {
					activityId: this.activity?.id,
				};
			},
			skip() {
				return !this.activity;
			},
			update: (data) =>
				data.project_activity_request_more_hours_aggregate.aggregate.sum
					.hours,
		},
		contractor_projects: {
			query: GET_CONTRACTOR_PROJECTS,
			variables() {
				return {
					where: {
						project_activities: {
							contractor: {
								user_id: {
									_eq: this.currentUser.id,
								},
							},
						},
					},
				};
			},
			update(data) {
				return data.project;
			},
		},
		allTrackedTimes: {
			query: GET_PROJECT_ACTIVITY_TIMES_BY_CONTRACTOR,
			variables() {
				return {
					userID: this.currentUser.id,
				};
			},
			update(data) {
				return data.project_activity_time;
			},
		},
		contractor_activities: {
			query: GET_CONTRACTOR_ACTIVITIES,
			variables() {
				return {
					where: {
						contractor: {
							user_id: { _eq: this.currentUser.id },
						},
						_and: {
							project: {
								id: { _eq: this.project[0]?.id },
							},
						},
						deleted: { _eq: false },
					},
				};
			},
			skip() {
				return !this.project;
			},
			update(data) {
				return data.project_activity;
			},
		},
	},
})
export default class TimesheetTimeTrackerModal extends Vue {
	@Prop({ type: Object, required: true }) readonly currentUser!: User;
	@Prop({ type: Object, required: false }) readonly activeActivity!: Project_Activity;
	@Prop({ type: Function, required: false }) checkDateWeekDisable?: Function;

	// State variables
	allTrackedTimes: Project_Activity_Time[] = [];
	rangeTracker = false;
	timerTracker = true;
	requestHoursSlot = false;
	loading = false;
	contractor_projects: Project[] = [];
	contractor_activities: Project_Activity[] = [];
	activity: Project_Activity = this.activeActivity ? this.activeActivity : null;
	project: DropdownOption[] = this.activeActivity
		? [{ name: this.activeActivity.project.name, id: this.activeActivity.project.id }]
		: [];

	// UI state
	activeClasses = 'activeClass';
	rangeTrackerClasses = '';
	timerTrackerClasses = '';
	requestHoursClasses = '';

	// Form inputs
	rangeStart: string = '00:00:00';
	rangeEnd: string = '00:00:01';
	tracked_date: Date = new Date();
	notes: string = '';
	reason: string = '';
	requestTimeInput: string = '00:00:00';

	// API data
	trackedTimes: Project_Activity_Time[] = undefined;
	requestedHours: Project_Activity_Request_More_Hours[] = undefined;
	id: string = '';
	status: string = 'CREATED';
	timeAggregate: number = 0;
	hoursAggregate: number = 0;

	// Timer state
	timerRunning: boolean = false;
	timerRecorded: boolean = false;
	timerStart: Date = null;
	timerStop: Date = null;
	timerInterval: number = null;
	processing: boolean = false;

	// Utility
	convertSecondsToReadable = convertSecondsToReadable;

	// Computed properties
	get project_options(): DropdownOption[] {



		return this.contractor_projects.map((project) => ({
			name: project.client_name,
			id: project.id
		}));
	}

	get activity_options(): DropdownOption[] {
		return this.contractor_activities.map((activity) => ({
			name: moment(activity.start_date).format('DD/MM/YYYY'),
			id: activity.id
		}));
	}

	get timeSelect(): number {
		if (this.rangeTracker) {
			return this.rangeTime;
		}
		if (this.timerTracker) {
			return this.timerTime;
		}
		if (this.requestHoursSlot) {
			return this.requestHoursTime;
		}
		return 0;
	}

	get project_option() {
		return this.project;
	}

	get activity_option() {
		if (this.activity) {
			return [{
				name: moment(this.activity.start_date).format('DD/MM/YYYY'),
				id: this.activity?.id
			}];
		}
		return [];
	}

	get canSave(): boolean {
		if (this.timerTracker && this.timerRecorded && this.timeSelect > 0) {
			return true;
		}
		if (this.rangeTracker && this.timeSelect && this.rangeStart < this.rangeEnd) {
			return true;
		}
		if (this.requestHoursSlot && this.timeSelect > 0 && this.reason) {
			return true;
		}
		return false;
	}

	get buttonText(): string {
		if (this.timerTracker && this.timerRunning) {
			return 'Stop Timer';
		}
		if (this.timerTracker && this.timerRecorded) {
			return 'Submit';
		}
		return 'Save';
	}

	get rangeTime(): number {
		if (!this.rangeStart || !this.rangeEnd) {
			return 0;
		}

		const normalizedStart = this.normalizeTimeString(this.rangeStart);
		const normalizedEnd = this.normalizeTimeString(this.rangeEnd);

		const start = normalizedStart.split(':');
		const end = normalizedEnd.split(':');

		const startDate = new Date(
			0, 0, 0,
			Number(start[0] || 0),
			Number(start[1] || 0),
			Number(start[2] || 0)
		);

		const endDate = new Date(
			0, 0, 0,
			Number(end[0] || 0),
			Number(end[1] || 0),
			Number(end[2] || 0)
		);

		const diff = endDate.getTime() - startDate.getTime();

		// Update the actual values to normalized ones
		this.rangeStart = normalizedStart;
		this.rangeEnd = normalizedEnd;

		return diff / 1000;
	}

	get requestHoursTime(): number {
		if (!this.requestTimeInput) {
			return 0;
		}

		const normalizedTime = this.normalizeTimeString(this.requestTimeInput);
		const times = normalizedTime.split(':');

		// Update to normalized value
		this.requestTimeInput = normalizedTime;

		return (
			Number(times[2] || 0) +
			Number(times[1] || 0) * 60 +
			Number(times[0] || 0) * 60 * 60
		);
	}

	get timerTime(): number {
		if (!this.timerStart || !this.timerStop) {
			return 0;
		}
		const diff = this.timerStop.getTime() - this.timerStart.getTime();
		return Math.floor(diff / 1000);
	}

	get getTitle() {

		return moment(this.activity?.start_date).format('DD/MM/YYYY')
	}

	get rangeEndHourRange(): Number[] {
		if (this.rangeStart) {
			const normalizedStart = this.normalizeTimeString(this.rangeStart);
			const startParts = normalizedStart.split(':');
			const startHour = Number(startParts[0] || 0);

			return [...Array(24 - startHour).keys()].map(i => i + startHour);
		}

		return [...Array(24).keys()];
	}

	get rangeEndMinuteRange(): Number[] {
		const hourEqual = this.rangeEnd.split(':')[0] === this.rangeStart.split(':')[0];

		if (this.rangeStart && hourEqual) {
			const normalizedStart = this.normalizeTimeString(this.rangeStart);
			const startParts = normalizedStart.split(':');
			const startMinute = Number(startParts[1] || 0);

			return [...Array(60 - startMinute).keys()].map(i => i + startMinute);
		}

		return [...Array(60).keys()];
	}

	get rangeEndSecondRange(): Number[] {
		const hourEqual = this.rangeEnd.split(':')[0] === this.rangeStart.split(':')[0];
		const minuteEqual = this.rangeEnd.split(':')[1] === this.rangeStart.split(':')[1];

		if (this.rangeStart && hourEqual && minuteEqual) {
			const normalizedStart = this.normalizeTimeString(this.rangeStart);
			const startParts = normalizedStart.split(':');
			const startSecond = Number(startParts[2] || 0) + 1;

			return [...Array(60 - startSecond).keys()].map(i => i + startSecond);
		}

		return [...Array(60).keys()];
	}

	get rangeEndError(): Boolean {
		if (this.rangeStart && this.rangeEnd) {
			const start = this.rangeStart.split(':');
			const end = this.rangeEnd.split(':');

			const sDate = new Date(
				0, 0, 0,
				Number(start[0] || 0),
				Number(start[1] || 0),
				Number(start[2] || 0)
			);

			const eDate = new Date(
				0, 0, 0,
				Number(end[0] || 0),
				Number(end[1] || 0),
				Number(end[2] || 0)
			);

			return !(eDate > sDate);
		}

		return false;
	}

	get showTrackingModes(): { title: string; project: string } {
		const timedActivity = localStorage.getItem('activity')
			? JSON.parse(localStorage.getItem('activity'))
			: undefined;

		if (!timedActivity || (timedActivity && timedActivity.id === this.activity?.id)) {
			return { title: '', project: '' };
		}

		return {
			title: timedActivity.title,
			project: timedActivity.project && timedActivity.project.name
				? timedActivity.project.name
				: '',
		};
	}

	// Methods
	isTimeAddedAlreadyStyle(requestHoursSlot, timeAggregate, hoursAggregate) {
		const hasTime = requestHoursSlot
			? convertSecondsToReadable(hoursAggregate) !== '00:00:00'
			: convertSecondsToReadable(timeAggregate) !== '00:00:00';

		return hasTime ? 'w-auto xl:border-r-1 xl:border-gray-300 pr-4' : 'w-full';
	}

	normalizeTimeString(timeString: string): string {
		return timeString
			.replace('HH', '00')
			.replace('mm', '00')
			.replace('ss', '00');
	}

	// Lifecycle methods
	created(): void {
		this.timerTrackerClasses = this.activeClasses;

		// Load state without starting anything yet
		this.loadTimerStateWithoutStarting();

		// Start timer interval only if it should be running
		if (this.timerRunning && this.timerStart) {
			this.startTimerDisplay();
		}
	}

	beforeDestroy(): void {
		this.clearTimerInterval();
	}

	// Timer state management
	loadTimerStateFromStorage(): void {
		const storedTimerRunning = localStorage.getItem('timerRunning') === '1';
		const storedTimerRecorded = localStorage.getItem('timerRecorded') === '1';
		const storedStartTime = localStorage.getItem('startTime');
		const storedStopTime = localStorage.getItem('stopTime');
		const storedActivity = localStorage.getItem('activity');

		this.timerRunning = storedTimerRunning;
		this.timerRecorded = storedTimerRecorded;

		// Only process timer data if we have valid timestamps
		if (storedStartTime) {
			this.timerStart = new Date(storedStartTime);

			if (storedStopTime) {
				this.timerStop = new Date(storedStopTime);
			}

			// Restore project and activity selections if timer data exists
			if (storedActivity) {
				try {
					const activityData = JSON.parse(storedActivity);

					// Set activity data
					this.activity = activityData;

					// Set project data
					if (activityData.project) {
						this.project = [{
							name: activityData.project.name,
							id: activityData.project.id
						}];
					}
				} catch (e) {
					console.error('Error parsing activity data from localStorage', e);
				}
			}

			// Only start timer display if timer should be running
			if (this.timerRunning) {
				this.startTimerDisplay();
			} else if (this.timerRecorded) {
				// If timer is stopped but recorded, show the elapsed time
				if (document.getElementById('modaltimerLabel')) {
					document.getElementById('modaltimerLabel').innerHTML = this.getTimer();
				}
			}
		}
	}


	clearTimerInterval(): void {
		if (this.timerInterval) {
			window.clearInterval(this.timerInterval);
			this.timerInterval = null;
		}
	}

	// Tab navigation
	showTimerSlot(): void {
		// Clear any existing timer interval first
		this.clearTimerInterval();

		// Switch tabs
		this.timerTracker = true;
		this.rangeTracker = false;
		this.requestHoursSlot = false;

		// Update UI classes
		this.timerTrackerClasses = this.activeClasses;
		this.rangeTrackerClasses = '';
		this.requestHoursClasses = '';

		// Load state but don't start anything yet
		this.loadTimerStateWithoutStarting();

		// Update display based on current state
		if (this.timerRunning && this.timerStart) {
			// Only if timer is supposed to be running, start the interval
			this.startTimerDisplay();
		} else if (this.timerRecorded && this.timerStart && this.timerStop) {
			// Just update display for recorded time
			if (document.getElementById('modaltimerLabel')) {
				document.getElementById('modaltimerLabel').innerHTML = this.getTimer();
			}
		} else {
			// Reset display
			if (document.getElementById('modaltimerLabel')) {
				document.getElementById('modaltimerLabel').innerHTML = '00:00:00';
			}
		}
	}

	updateTimerDisplay(): void {
		// If timer is already running, make sure display is updating
		if (this.timerRunning && this.timerStart) {
			this.startTimerDisplay();
		}
		// If timer is stopped but recorded, show the elapsed time
		else if (this.timerRecorded && this.timerStart && this.timerStop) {
			if (document.getElementById('modaltimerLabel')) {
				document.getElementById('modaltimerLabel').innerHTML = this.getTimer();
			}
		}
		// Otherwise reset to zeros
		else {
			if (document.getElementById('modaltimerLabel')) {
				document.getElementById('modaltimerLabel').innerHTML = '00:00:00';
			}
		}
	}

	loadTimerStateWithoutStarting(): void {
		const storedTimerRunning = localStorage.getItem('timerRunning') === '1';
		const storedTimerRecorded = localStorage.getItem('timerRecorded') === '1';
		const storedStartTime = localStorage.getItem('startTime');
		const storedStopTime = localStorage.getItem('stopTime');
		const storedActivity = localStorage.getItem('activity');

		// Set state without starting anything
		if (storedStartTime) {
			this.timerStart = new Date(storedStartTime);

			if (storedStopTime) {
				this.timerStop = new Date(storedStopTime);
			}

			this.timerRunning = storedTimerRunning;
			this.timerRecorded = storedTimerRecorded;

			// Load activity data if available
			if (storedActivity) {
				try {
					const activityData = JSON.parse(storedActivity);
					this.activity = activityData;

					if (activityData.project) {
						this.project = [{
							name: activityData.project.name,
							id: activityData.project.id
						}];
					}
				} catch (e) {
					console.error('Error parsing activity data', e);
				}
			}
		}
	}
	showRangeSlot(): void {
		// Clear any existing timer first
		this.clearTimerInterval();

		// Switch tabs
		this.timerTracker = false;
		this.rangeTracker = true;
		this.requestHoursSlot = false;

		// Update UI
		this.rangeTrackerClasses = this.activeClasses;
		this.timerTrackerClasses = '';
		this.requestHoursClasses = '';
	}


	showRequestHoursSlot(): void {
		// Clear any existing timer first
		this.clearTimerInterval();

		// Switch tabs
		this.timerTracker = false;
		this.rangeTracker = false;
		this.requestHoursSlot = true;

		// Update UI
		this.rangeTrackerClasses = '';
		this.timerTrackerClasses = '';
		this.requestHoursClasses = this.activeClasses;
	}
	// Selection handlers
	setProject(input: DropdownOption[]): void {
		this.project = input;
		this.activity = null;
		this.$apollo.queries.contractor_activities.refetch();
	}

	setActivities(input: DropdownOption[]): void {
		this.activity = this.contractor_activities.find(
			(activity) => activity.id === input[0].id
		);
	}

	// Timer operations
	startTimerDisplay(): void {
		// Only start if timer should be running
		if (!this.timerRunning || !this.timerStart) {
			return;
		}

		// Clear any existing interval
		this.clearTimerInterval();

		// Start new interval
		this.timerInterval = window.setInterval(() => {
			const timerLabel = document.getElementById('modaltimerLabel');
			if (timerLabel) {
				timerLabel.innerHTML = this.getTimer();
			}
		}, 1000);
	}


	handleTimerAction(): void {
		const timedActivity = localStorage.getItem('activity')
			? JSON.parse(localStorage.getItem('activity'))
			: undefined;

		// Check if timesheet is disabled for this date
		if (this.checkDateWeekDisable) {
			this.checkDateWeekDisable(this.tracked_date, this.activity).then(({ isDisable, id: timesheetId }) => {
				if (isDisable) {
					this.$eventBus.emit(
						NOTIFICATION,
						Message.errorMessage(
							"The timesheet status must be open for the period: " + this.tracked_date.toDateString()
						)
					);
					return;
				}

				this.toggleTimer(timedActivity, timesheetId);
			});
		} else {
			this.toggleTimer(timedActivity);
		}
	}

	toggleTimer(timedActivity, timesheetId?: string): void {
		// Only proceed if no other activity is being tracked
		// or if this is the same activity
		if (!timedActivity || (timedActivity && timedActivity.id === this.activity?.id)) {
			if (this.timerRunning) {
				// Stop timer
				this.stopTimer();
			} else {
				// Start timer
				this.startTimer(timesheetId);
			}
		} else {
			// Another activity is being tracked
			this.$eventBus.emit(
				NOTIFICATION,
				Message.errorMessage(
					`A timer is already running for task '${timedActivity.title}'. Please attend to that first.`
				)
			);
		}
	}

	startTimer(timesheetId?: string): void {
		// Initialize timer state
		this.timerRunning = true;
		this.timerStart = new Date();
		this.timerRecorded = false;

		// Store in localStorage
		localStorage.setItem('startTime', this.timerStart.toString());
		localStorage.setItem('timerRunning', '1');
		localStorage.setItem('timerRecorded', '0');

		// Make sure we store complete activity data including the project
		const activityToStore = {
			...this.activity,
			project: {
				id: this.project[0]?.id,
				name: this.project[0]?.name
			}
		};

		// Store the activity with any additional data
		if (timesheetId) {
			localStorage.setItem('activity', JSON.stringify({
				...activityToStore,
				timesheetId
			}));
		} else {
			localStorage.setItem('activity', JSON.stringify(activityToStore));
		}

		// Store tracker owner
		localStorage.setItem('timerOwner' + this.currentUser.id, '1');

		// Start the timer display
		this.startTimerDisplay();
	}


	stopTimer(): void {
		// Stop the timer
		this.timerRunning = false;
		this.timerStop = new Date();
		this.timerRecorded = true;

		// Update localStorage
		localStorage.setItem('stopTime', this.timerStop.toString());
		localStorage.setItem('timerRunning', '0');
		localStorage.setItem('timerRecorded', '1');

		// Clear interval
		this.clearTimerInterval();

		// Notify other components
		this.$root.$emit('timerStopped');
	}

	getTimer(): string {
		let secsDiff = 0;

		if (!this.timerRunning && this.timerStart && this.timerStop) {
			// Timer is stopped, calculate fixed difference
			secsDiff = (this.timerStop.getTime() - this.timerStart.getTime()) / 1000;
		} else if (this.timerStart && this.timerRunning) {
			// Timer is running, calculate current difference
			secsDiff = (new Date().getTime() - this.timerStart.getTime()) / 1000;
		}

		return this.convertSecondsToReadable(secsDiff);
	}
	discardTimer(): void {
		// Change to fully reset timer without starting new one
		// Save the current selections
		const currentActivity = this.activity;
		const currentProject = this.project;

		// Clear any existing intervals
		this.clearTimerInterval();

		// Reset timer state with fresh timestamp but don't start it
		this.timerStart = new Date();
		this.timerStop = null;
		this.timerRunning = true; // Set to true to indicate it's running
		this.timerRecorded = false;

		// Update localStorage
		localStorage.setItem('startTime', this.timerStart.toString());
		localStorage.setItem('timerRunning', '1');
		localStorage.removeItem('stopTime');
		localStorage.removeItem('timerRecorded');

		// Keep the same activity/project
		if (currentActivity) {
			localStorage.setItem('activity', JSON.stringify({
				...currentActivity,
				project: {
					id: currentProject[0]?.id,
					name: currentProject[0]?.name
				}
			}));
		}

		// Start the timer display
		this.startTimerDisplay();
	}
	resumeTimer(): void {
		// Save the current selections and previous time
		const currentActivity = this.activity;
		const currentProject = this.project;
		const previousElapsedTime = this.timerTime; // Save the elapsed time so far

		// Clear existing interval
		this.clearTimerInterval();

		// Calculate a virtual start time that will preserve the elapsed time
		const now = new Date();
		const virtualStartTime = new Date(now.getTime() - (previousElapsedTime * 1000));

		// Update timer state
		this.timerStart = virtualStartTime;
		this.timerStop = null;
		this.timerRunning = true;
		this.timerRecorded = false;

		// Update localStorage
		localStorage.setItem('startTime', virtualStartTime.toString());
		localStorage.setItem('timerRunning', '1');
		localStorage.removeItem('stopTime');
		localStorage.removeItem('timerRecorded');

		// Keep the same activity/project for the timer
		if (currentActivity) {
			localStorage.setItem('activity', JSON.stringify({
				...currentActivity,
				project: {
					id: currentProject[0]?.id,
					name: currentProject[0]?.name
				}
			}));
		}

		// Start the timer display again
		this.startTimerDisplay();

		// Notify other components
		this.$root.$emit('timerResumed');
	}

	// Button handlers
	saveTimerData(): void {
		if (this.timerRunning) {
			// Stop running timer first
			this.handleTimerAction();
		} else if (this.timerRecorded) {
			// Save recorded time
			this.saveTimeTrack();
		} else {
			// Start timer
			this.handleTimerAction();
		}
	}

	closeModal(): void {
		this.$emit('close');
	}

	resetPage(): void {
		// Only reset timer if not running
		if (!this.timerRunning) {
			// Reset form fields
			this.rangeStart = '00:00:00';
			this.rangeEnd = '00:00:01';
			this.notes = '';
			this.tracked_date = new Date();
			this.requestTimeInput = '00:00:00';
			this.id = '';
			this.status = 'CREATED';
			this.reason = '';
			this.processing = false;

			// Clear timer
			this.cleanupTimerCompletely();
		}

		// Refresh data
		this.refetchData();
	}

	// Data operations
	async saveTimeTrack(): Promise<void> {
		// Check if timesheet is disabled for this date
		if (this.checkDateWeekDisable) {
			const { isDisable, id } = await this.checkDateWeekDisable(this.tracked_date, this.activity);

			if (isDisable) {
				this.$eventBus.emit(
					NOTIFICATION,
					Message.errorMessage(
						"The timesheet status must be open for the period: " + this.tracked_date.toDateString()
					)
				);
				return;
			}

			// Proceed with save but check for time overlap
			if (this.checkForTimeOverlap()) {
				return;
			}

			this.processing = true;

			try {
				if (!this.id) {
					// Insert new time record
					await this.createTimeRecord(id);
				} else {
					// Update existing time record
					await this.updateTimeRecord(id);
				}

				// Completely clear timer state but keep the page intact
				this.cleanupTimerCompletely();

				// Refresh data
				this.$apollo.queries.allTrackedTimes.refetch();

				// Close the modal
				this.$emit('close');
			} catch (error) {
				console.error('Error saving time:', error);
				this.$eventBus.emit(
					NOTIFICATION,
					Message.errorMessage('An error occurred while saving time')
				);
			} finally {
				this.processing = false;
			}
		} else {
			// No date check function, proceed with regular save
			if (this.checkForTimeOverlap()) {
				return;
			}

			this.processing = true;

			try {
				if (!this.id) {
					// Insert new time record without timesheet ID
					await this.createTimeRecord();
				} else {
					// Update existing time record without timesheet ID
					await this.updateTimeRecord();
				}

				// Completely clear timer state but keep the page intact
				this.cleanupTimerCompletely();

				// Refresh data
				this.$apollo.queries.allTrackedTimes.refetch();

				// Close the modal
				this.$emit('close');
			} catch (error) {
				console.error('Error saving time:', error);
				this.$eventBus.emit(
					NOTIFICATION,
					Message.errorMessage('An error occurred while saving time')
				);
			} finally {
				this.processing = false;
			}
		}
	}

	// Add this new method to completely clean up after submission
	cleanupTimerCompletely(): void {
		// Reset timer state
		this.timerStart = null;
		this.timerStop = null;
		this.timerRunning = false;
		this.timerRecorded = false;

		// Clear all timer-related localStorage items
		localStorage.removeItem('startTime');
		localStorage.removeItem('stopTime');
		localStorage.removeItem('timerRecorded');
		localStorage.removeItem('timerRunning');
		localStorage.removeItem('activity');
		localStorage.removeItem('timerOwner' + this.currentUser.id);
		localStorage.removeItem('timerInterval');

		// Clear any running intervals
		this.clearTimerInterval();

		// Reset timer display
		if (document.getElementById('modaltimerLabel')) {
			document.getElementById('modaltimerLabel').innerHTML = '00:00:00';
		}

		// Notify other components
		this.$root.$emit('timerStopped');
	}
	async createTimeRecord(timesheetId?: string): Promise<void> {
		const trackedDate = moment(this.tracked_date).local().format('YYYY-MM-DD');

		// For timer tab, use timer values
		const startTime = this.timerTracker
			? moment(this.timerStart).format('HH:mm:ss')
			: this.rangeStart;

		const endTime = this.timerTracker
			? moment(this.timerStop).format('HH:mm:ss')
			: this.rangeEnd;

		const projectActivityTime = {
			activity_id: this.activity?.id,
			contractor_id: this.activity?.contractor_id,
			start_time: startTime,
			end_time: endTime,
			notes: this.notes,
			time: this.timeSelect,
			timesheet_id: (this.activity as any)?.timesheet_id || timesheetId,
			tracked_date: trackedDate,
		};

		await this.$apollo.mutate({
			mutation: INSERT_PROJECT_ACTIVITY_TIME,
			variables: {
				projectActivityTime,
			},
		});

		this.$eventBus.emit(
			NOTIFICATION,
			Message.successMessage('Time tracked has been successfully added')
		);
	}

	async updateTimeRecord(timesheetId?: string): Promise<void> {
		const obj = {
			time: this.timeSelect,
			start_time: this.rangeStart,
			end_time: this.rangeEnd,
			status: this.status,
			notes: this.notes,
			tracked_date: this.tracked_date,
			timesheet_id: (this.activity as any)?.timesheet_id || timesheetId,
		};

		await this.$apollo.mutate({
			mutation: UPDATE_PROJECT_ACTIVITY_TIME,
			variables: {
				id: this.id,
				obj,
			},
		});

		this.$eventBus.emit(
			NOTIFICATION,
			Message.successMessage('Time tracked has been successfully updated')
		);
	}

	checkForTimeOverlap(): boolean {
		// Skip overlap check for timer mode - assumed user knows what they're doing
		if (this.timerTracker) return false;

		let overlap = false;

		// Get the date we're checking in YYYY// Get the date we're checking in YYYY-MM-DD format
		const trackedDateStr = moment(this.tracked_date).format('YYYY-MM-DD');

		this.allTrackedTimes.forEach((time) => {
			// Skip current record when checking for overlaps
			if (this.id !== time.id) {
				// Only check times for the same date
				if (time.tracked_date === trackedDateStr) {
					const isStartInExistingRange = this.rangeStart > time.start_time && this.rangeStart < time.end_time;
					const isEndInExistingRange = this.rangeEnd > time.start_time && this.rangeEnd < time.end_time;
					const isExistingInNewRange = this.rangeStart < time.start_time && this.rangeEnd > time.end_time;

					if (isStartInExistingRange || isEndInExistingRange || isExistingInNewRange) {
						overlap = true;
						this.$eventBus.emit(
							NOTIFICATION,
							Message.errorMessage(`Time overlap with ${time.project_activity.title}`)
						);
						return true;
					}
				}
			}
		});

		return overlap;
	}

	deleteTrackedTime(id: string): void {
		this.$confirm({
			title: `Delete tracked time for task ${this.activity?.title}`,
			message: 'Are you sure?',
			button: {
				no: 'No',
				yes: 'Yes',
			},
			callback: async (confirm: boolean) => {
				if (confirm) {
					try {
						await this.$apollo.mutate({
							mutation: DELETE_PROJECT_ACTIVITY_TIME,
							variables: { id },
						});

						this.$eventBus.emit(
							NOTIFICATION,
							Message.successMessage('Time tracking entry deleted successfully')
						);

						this.refetchData();
					} catch (error) {
						console.error('Error deleting tracked time:', error);
						this.$eventBus.emit(
							NOTIFICATION,
							Message.errorMessage('Error deleting tracked time')
						);
					}
				}
			},
		});
	}

	deleteRequestedHours(id: string): void {
		this.$confirm({
			title: `Delete requested hours for task ${this.activity?.title}`,
			message: 'Are you sure?',
			button: {
				no: 'No',
				yes: 'Yes',
			},
			callback: async (confirm: boolean) => {
				if (confirm) {
					try {
						await this.$apollo.mutate({
							mutation: DELETE_PROJECT_ACTIVITY_REQUEST_MORE_HOURS,
							variables: { id },
						});

						this.$eventBus.emit(
							NOTIFICATION,
							Message.successMessage('Hours request deleted successfully')
						);

						this.refetchData();
					} catch (error) {
						console.error('Error deleting requested hours:', error);
						this.$eventBus.emit(
							NOTIFICATION,
							Message.errorMessage('Error deleting hours request')
						);
					}
				}
			},
		});
	}

	refetchData(): void {
		this.$apollo.queries.trackedTimes.refetch();
		this.$apollo.queries.timeAggregate.refetch();
		this.$apollo.queries.requestedHours.refetch();
		this.$apollo.queries.hoursAggregate.refetch();
		this.$emit('timesChanged');
	}

	// Utility methods
	convertDateString(date: string): string {
		const parts = date.split('-');
		return parts[2] + '/' + parts[1] + '/' + parts[0];
	}

	setToEdit(time: Project_Activity_Time): void {
		this.showRangeSlot();
		this.id = time.id;
		this.notes = time.notes;
		this.tracked_date = new Date(moment(time.tracked_date).local().toDate());
		this.rangeStart = time.start_time;
		this.rangeEnd = time.end_time;
		this.status = time.status;
	}

	setToEditRequestedHours(time: Project_Activity_Request_More_Hours): void {
		this.showRequestHoursSlot();
		this.id = time.id;
		this.reason = time.reason;
		this.status = time.status;
		this.requestTimeInput = this.convertSecondsToReadable(time.hours);
	}

	async sendRequest(event): Promise<void> {
		if (event) {
			event.preventDefault();
		}
		this.processing = true;

		try {
			if (!this.id) {
				// Create new request
				const projectActivityRequestedHours = {
					hours: this.timeSelect,
					reason: this.reason,
					requester_id: this.currentUser.id,
					activity_id: this.activity?.id,
				};

				await this.$apollo.mutate({
					mutation: INSERT_PROJECT_ACTIVITY_MORE_HOURS_REQUEST,
					variables: {
						projectActivityRequestedHours,
					},
				});

				this.$eventBus.emit(
					NOTIFICATION,
					Message.successMessage('Hours request has been successfully submitted')
				);
			} else {
				// Update existing request
				const obj = {
					hours: this.timeSelect,
					status: this.status,
					reason: this.reason,
				};

				await this.$apollo.mutate({
					mutation: UPDATE_PROJECT_ACTIVITY_REQUEST_MORE_HOURS,
					variables: {
						id: this.id,
						obj,
					},
				});

				this.$eventBus.emit(
					NOTIFICATION,
					Message.successMessage('Hours request has been successfully updated')
				);
			}

			this.resetPage();
		} catch (error) {
			console.error('Error sending hours request:', error);
			this.$eventBus.emit(
				NOTIFICATION,
				Message.errorMessage('An error occurred while submitting hours request')
			);
		} finally {
			this.processing = false;
		}
	}
}
